import { removeDiacritics } from '@mahawi/eshop-common/src/remove-diacritics';
import {
  EWarehouseCategorySuggestionKind,
  type IAdminCategory,
  type ITranslation,
  type IWarehouseCategorySuggestion,
} from '@mahawi/eshop-common/src/types';

export enum EWarehouseCategorySuggestionNameConflict {
  EXISTING_CATEGORY = 'existingCategory',
  OTHER_PROPOSAL = 'otherProposal',
}

export interface IWarehouseCategorySuggestionNameCheck {
  groupUuids: string[];
  conflict?: EWarehouseCategorySuggestionNameConflict;
  existingCategoryUuid?: string;
}

export const WAREHOUSE_CATEGORY_SUGGESTION_STAGE_LABELS: Record<
  EWarehouseCategorySuggestionKind,
  string
> = {
  [EWarehouseCategorySuggestionKind.PAIR_EXISTING]: 'Pair with existing',
  [EWarehouseCategorySuggestionKind.CREATE_CHILD]: 'New child categories',
  [EWarehouseCategorySuggestionKind.CREATE_PARENT]: 'New parent categories',
};

export const normalizeCategoryName = (name: string): string =>
  removeDiacritics(name.trim().toLowerCase());

const getProposedParentUuid = (
  suggestion: IWarehouseCategorySuggestion,
): string | null =>
  suggestion.kind === EWarehouseCategorySuggestionKind.CREATE_CHILD
    ? (suggestion.parentCategoryUuid ?? null)
    : null;

const isCreateSuggestion = (
  suggestion: IWarehouseCategorySuggestion,
): boolean =>
  suggestion.kind !== EWarehouseCategorySuggestionKind.PAIR_EXISTING;

export const getWarehouseCategorySuggestionNameChecks = ({
  suggestions,
  getName,
  categories,
  languageTypeCode,
}: {
  suggestions: IWarehouseCategorySuggestion[];
  getName: (suggestion: IWarehouseCategorySuggestion) => string;
  categories: IAdminCategory[];
  languageTypeCode: string | undefined;
}): Map<string, IWarehouseCategorySuggestionNameCheck> => {
  const createSuggestions = suggestions
    .filter(isCreateSuggestion)
    .map((suggestion: IWarehouseCategorySuggestion) => ({
      uuid: suggestion.warehouseCategory.uuid,
      normalizedName: normalizeCategoryName(getName(suggestion)),
      parentUuid: getProposedParentUuid(suggestion),
    }));

  return new Map<string, IWarehouseCategorySuggestionNameCheck>(
    suggestions.map(
      (
        suggestion: IWarehouseCategorySuggestion,
      ): [string, IWarehouseCategorySuggestionNameCheck] => {
        const uuid: string = suggestion.warehouseCategory.uuid;
        const normalizedName: string = normalizeCategoryName(
          getName(suggestion),
        );

        if (!isCreateSuggestion(suggestion) || !normalizedName) {
          return [uuid, { groupUuids: [uuid] }];
        }

        const parentUuid: string | null = getProposedParentUuid(suggestion);
        const sameName = createSuggestions.filter(
          (other): boolean => other.normalizedName === normalizedName,
        );
        const groupUuids: string[] = sameName
          .filter((other): boolean => other.parentUuid === parentUuid)
          .map(({ uuid: otherUuid }): string => otherUuid);

        const existingCategory: IAdminCategory | undefined = categories.find(
          (category: IAdminCategory): boolean =>
            category.names.some(
              ({ code, value }: ITranslation): boolean =>
                code === languageTypeCode &&
                normalizeCategoryName(value) === normalizedName,
            ),
        );

        if (existingCategory) {
          return (existingCategory.parentCategoryUUID ?? null) === parentUuid
            ? [
                uuid,
                { groupUuids, existingCategoryUuid: existingCategory.uuid },
              ]
            : [
                uuid,
                {
                  groupUuids,
                  conflict:
                    EWarehouseCategorySuggestionNameConflict.EXISTING_CATEGORY,
                },
              ];
        }

        if (
          sameName.some((other): boolean => other.parentUuid !== parentUuid)
        ) {
          return [
            uuid,
            {
              groupUuids,
              conflict: EWarehouseCategorySuggestionNameConflict.OTHER_PROPOSAL,
            },
          ];
        }

        return [uuid, { groupUuids }];
      },
    ),
  );
};
